import "zod/compile";
import { oc } from "@orpc/contract";
import { z } from "zod";
import {
  CodeSchemeDraftSchema,
  CodeSchemeKeySchema,
  CreateSchemeIssueSchema,
  ShortCodeEntityTypeSchema,
  StoredCodeSchemeSchema,
} from "@erp/shared-types";

/**
 * Code Schemes (docs/short-codes/CONTEXT.md). Listing, creating
 * and choosing the display scheme need the CodeScheme permission, because a
 * scheme changes how every code of an entity type reads.
 */
export const codeSchemesContracts = {
  list: oc
    .input(z.object({ entityType: ShortCodeEntityTypeSchema.optional() }))
    .output(z.object({ schemes: z.array(StoredCodeSchemeSchema) }))
    .route({
      method: "GET",
      summary: "List code schemes",
      description:
        "Lists the organization's Code Schemes, optionally for one entity type, with their segments and which one supplies Display Codes.",
    }),
  create: oc
    .input(CodeSchemeDraftSchema)
    .output(
      z.union([
        z.object({ scheme: StoredCodeSchemeSchema }),
        z.object({ issues: z.array(CreateSchemeIssueSchema) }),
      ]),
    )
    .route({
      method: "POST",
      summary: "Create a code scheme",
      description:
        "Saves a new Code Scheme for an entity type, or returns every reason it cannot be saved (unsafe part pattern, unknown relation, missing referenced scheme, reference cycle, key already used).",
    }),
  setDisplay: oc
    .input(
      z.object({
        entityType: ShortCodeEntityTypeSchema,
        key: CodeSchemeKeySchema.nullable(),
      }),
    )
    .output(z.object({ ok: z.literal(true) }))
    .route({
      method: "POST",
      summary: "Choose the display code scheme",
      description:
        "Chooses the Code Scheme whose active codes are shown next to an entity type's names, or none.",
    }),
} as const;

/** Short Codes. Reading them needs the entity type's own read permission. */
export const shortCodesContracts = {
  search: oc
    .input(
      z.object({
        entityType: ShortCodeEntityTypeSchema,
        text: z.string().trim().min(1).max(255),
        limit: z.int().min(1).max(500).default(50),
      }),
    )
    .output(z.object({ entityIds: z.array(z.uuid()) }))
    .route({
      method: "GET",
      summary: "Search short codes",
      description:
        "Returns the ids of entities whose Short Codes, active or retired, contain the text, ignoring case: exact matches first, then prefixes, then the rest.",
    }),
} as const;
